import { useEffect, useState, useRef } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const PortfolioCard = ({ item, index }) => {
  const [visible, setVisible] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleClick = () => {
    if (item.link_url) {
      window.open(item.link_url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div
      ref={cardRef}
      onClick={handleClick}
      className={`group relative p-8 rounded-2xl backdrop-blur-xl bg-white/[0.03] border border-white/10 hover:border-white/20 transition-all duration-500 hover:scale-[1.02] transform ${
        visible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
      } ${item.link_url ? "cursor-pointer" : ""}`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 rounded-2xl overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#00F0FF]/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        {/* Neural network pattern */}
        <svg className="absolute inset-0 w-full h-full opacity-[0.03] group-hover:opacity-[0.06] transition-opacity duration-500">
          <pattern id={`grid-${item.company_id || item.id}`} width="40" height="40" patternUnits="userSpaceOnUse">
            <circle cx="20" cy="20" r="1" fill="currentColor" />
          </pattern>
          <rect width="100%" height="100%" fill={`url(#grid-${item.company_id || item.id})`} />
        </svg>
      </div>

      {/* Content */}
      <div className="relative z-10">
        {/* Category */}
        <span className="inline-block font-mono text-[10px] tracking-[0.3em] text-[#00F0FF] border border-[#00F0FF]/30 px-2 py-1 rounded-sm mb-4">
          {item.category}
        </span>

        {/* Company Name */}
        <h4 className="font-mono text-xl text-[#F3F4F6] tracking-wide mb-3 group-hover:text-[#00F0FF] transition-colors duration-300">
          {item.name}
        </h4>

        {/* Description */}
        <p className="font-sans text-[#6B7280] text-sm leading-relaxed">
          {item.description}
        </p>
        
        {/* Link indicator */}
        {item.link_url && (
          <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <svg className="w-4 h-4 text-[#00F0FF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </div>
        )}
      </div>
    </div>
  );
};

const PortfolioSection = () => {
  const [titleVisible, setTitleVisible] = useState(true);  // Start visible
  const [portfolioEfficiency, setPortfolioEfficiency] = useState([]);
  const [portfolioHuman, setPortfolioHuman] = useState([]);
  const [loading, setLoading] = useState(true);
  const titleRef = useRef(null);

  useEffect(() => {
    const fetchPortfolio = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/portfolio/companies`);
        if (response.ok) {
          const companies = await response.json();
          // Separate by thesis layer
          const efficiency = companies
            .filter(c => c.thesis_layer === 'efficiency')
            .sort((a, b) => (a.order || 0) - (b.order || 0));
          const human = companies
            .filter(c => c.thesis_layer === 'human')
            .sort((a, b) => (a.order || 0) - (b.order || 0));
          
          setPortfolioEfficiency(efficiency);
          setPortfolioHuman(human);
        }
      } catch (error) {
        console.error('Failed to fetch portfolio:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPortfolio();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTitleVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (titleRef.current) {
      observer.observe(titleRef.current);
    }

    return () => observer.disconnect();
  }, []);

  if (loading) {
    return (
      <section id="portfolio" className="py-32 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-white/10 rounded w-64 mb-12"></div>
            <div className="grid md:grid-cols-2 gap-6">
              {[1, 2].map(i => (
                <div key={i} className="h-48 bg-white/5 rounded-2xl"></div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="portfolio" className="py-32 px-6">
      <div className="max-w-6xl mx-auto">
        {/* The Efficiency Layer */}
        {portfolioEfficiency.length > 0 && (
          <div ref={titleRef} className="mb-24">
            <div
              className={`mb-12 transform transition-all duration-700 ${
                titleVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
              }`}
            >
              <h2 className="font-mono text-2xl md:text-3xl text-[#F3F4F6] tracking-wide">
                Portfolio: <span className="text-[#00F0FF]">The Efficiency Layer</span>
              </h2>
              <div className="w-24 h-[1px] bg-gradient-to-r from-[#00F0FF]/50 to-transparent mt-4" />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {portfolioEfficiency.map((item, index) => (
                <PortfolioCard key={item.company_id} item={item} index={index} />
              ))}
            </div>
          </div>
        )}

        {/* The Human Layer */}
        {portfolioHuman.length > 0 && (
          <div>
            <div
              className={`mb-12 transform transition-all duration-700 delay-200 ${
                titleVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
              }`}
            >
              <h2 className="font-mono text-2xl md:text-3xl text-[#F3F4F6] tracking-wide">
                Portfolio: <span className="text-orange-400">The Human Layer</span>
              </h2>
              <div className="w-24 h-[1px] bg-gradient-to-r from-orange-400/50 to-transparent mt-4" />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {portfolioHuman.map((item, index) => (
                <PortfolioCard key={item.company_id} item={item} index={index + portfolioEfficiency.length} />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default PortfolioSection;
